/**
 * Resolving which components belong to which builds.
 *
 * There are two records of that relationship:
 *
 *  - `build_components` — the real join table. Everything that edits a BOM
 *    (BuildDetailPage, ConfiguratorPage, AssemblyOrdersPage) writes here.
 *  - `components.build_sku` — a legacy comma-separated string on the component
 *    row, populated by an early import and never maintained since. Nothing in
 *    the app writes it.
 *
 * The rest of the app already reads the join table first and falls back to the
 * legacy column (see PrintSpecSheet, PrintMarketingTag, demoStations). These
 * helpers let the components page do the same, so builds created after the
 * legacy column stopped being maintained are still found.
 */

/** A build token is either an exact SKU or a `PREFIX*` wildcard. */
export function tokenMatchesBuildSku(token: string, sku: string): boolean {
  const t = token.trim().toLowerCase();
  const s = sku.trim().toLowerCase();
  if (!t || !s) return false;
  if (t.endsWith('*')) return s.includes(t.slice(0, -1));
  return s === t;
}

/**
 * Match a token against the legacy comma-separated `components.build_sku`.
 * The column appends a `-EOL` suffix to mark a retired pairing, which is not
 * part of the SKU and is stripped before comparing.
 */
export function tokenMatchesLegacyColumn(token: string, buildSkuColumn: string | null): boolean {
  if (!buildSkuColumn) return false;
  const segments = buildSkuColumn.split(',').map(s => s.trim().replace(/-eol$/i, ''));
  return segments.some(segment => tokenMatchesBuildSku(token, segment));
}

export interface BuildRow { id: string; sku: string }
export interface BomRow { build_id: string; component_sku: string }

/**
 * Map each component SKU in the given BOM rows to the indices of the tokens it
 * satisfies. The indices drive the coloured token pills on the components page,
 * so a component matching two tokens must report both.
 *
 * Keys are lower-cased: `build_components.component_sku` is free text and does
 * not always agree with `components.part_number` on casing.
 */
export interface BomTokenMatch {
  /** Component SKU (lower-cased) → indices of the tokens it satisfies. */
  tokensBySku: Map<string, number[]>;
  /** The same SKUs in their original casing, for exact-match DB filters. */
  skus: string[];
}

export function mapSkusToTokens(
  tokens: string[],
  builds: BuildRow[],
  bomRows: BomRow[],
): BomTokenMatch {
  const tokensByBuildId = new Map<string, number[]>();
  for (const build of builds) {
    const indices = tokens
      .map((token, idx) => (tokenMatchesBuildSku(token, build.sku) ? idx : -1))
      .filter(idx => idx !== -1);
    if (indices.length) tokensByBuildId.set(build.id, indices);
  }

  const tokensBySku = new Map<string, number[]>();
  const originalBySku = new Map<string, string>();

  for (const row of bomRows) {
    const indices = tokensByBuildId.get(row.build_id);
    if (!indices || !row.component_sku) continue;

    const original = row.component_sku.trim();
    const key = original.toLowerCase();
    if (!originalBySku.has(key)) originalBySku.set(key, original);

    const existing = tokensBySku.get(key);
    if (!existing) {
      tokensBySku.set(key, [...indices]);
      continue;
    }
    for (const idx of indices) if (!existing.includes(idx)) existing.push(idx);
  }

  for (const indices of tokensBySku.values()) indices.sort((a, b) => a - b);
  return { tokensBySku, skus: [...originalBySku.values()] };
}

/**
 * Which tokens does this component satisfy, counting both the join table and
 * the legacy column? Returns indices into `tokens`, empty if it matches none.
 */
export function matchingTokenIndices(
  tokens: string[],
  component: { part_number: string; build_sku?: string | null },
  skusFromBom: Map<string, number[]>,
): number[] {
  const fromBom = skusFromBom.get(component.part_number.trim().toLowerCase()) ?? [];
  const matched = new Set(fromBom);

  tokens.forEach((token, idx) => {
    if (tokenMatchesLegacyColumn(token, component.build_sku ?? null)) matched.add(idx);
  });

  return [...matched].sort((a, b) => a - b);
}
